import { ResponseMessage } from './types';

function resolveApiBaseUrl(apiUrl: string, apiBaseUrl?: string | null): string {
  const explicitBase = (apiBaseUrl || '').trim();
  if (explicitBase) {
    return explicitBase.replace(/\/$/, '');
  }

  try {
    const parsedUrl = new URL(apiUrl);
    const apiV1Index = parsedUrl.pathname.indexOf('/api/v1');
    if (apiV1Index >= 0) {
      return `${parsedUrl.origin}${parsedUrl.pathname.slice(0, apiV1Index + '/api/v1'.length)}`;
    }
    return parsedUrl.origin + '/api/v1';
  } catch {
    const normalized = apiUrl.trim().replace(/\/$/, '');
    const apiV1Index = normalized.indexOf('/api/v1');
    if (apiV1Index >= 0) {
      return normalized.slice(0, apiV1Index + '/api/v1'.length);
    }
    return normalized.replace(/\/webhook(?:\/.*)?$/, '') + '/api/v1';
  }
}

async function fetchChatResponse(
  url: string,
  sessionId: string,
  data: string,
  uniqueId: string,
  chatCount = 0
): Promise<ResponseMessage> {
  try {
    const body = {
      sessionId: sessionId,
      action: 'sendMessage',
      chatInput: data,
      uniqueId: uniqueId,
      chatCount,
    };
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (uniqueId) {
      headers['x-widget-key'] = uniqueId;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    const text = await response.text();

    if (response.ok) {
      //Trim the spaces leading and trailing
      if (!text.trim())
        return {
          content: `No Data`,
        };

      //parse the recieved JSON to the required format
      try {
        const result = JSON.parse(text);
        return {
          content: result['output'],
          handoff: result['handoff']
            ? {
                active: Boolean(result['handoff']?.active ?? true),
                status: result['handoff']?.status,
              }
            : undefined,
          isGreeting: Boolean(result['isGreeting']),
        };
      } catch (err) {
        console.error('Invalid JSON:', text);
      }
    } else {
      // Handle error responses (non-2xx status codes)
      try {
        const errorResult = JSON.parse(text);

        // Check for rate limit error (429 status code)
        if (response.status === 429 && errorResult.message) {
          return {
            content: errorResult.message,
          };
        }

        // Handle other error responses with a message field
        if (errorResult.message) {
          return {
            content: errorResult.message,
          };
        }
      } catch (err) {
        console.error('Error parsing error response:', text);
      }
    }

    return {
      content: "Sorry, Didn't get that. Please try again.",
    };
  } catch (err) {
    return {
      content: "Sorry, Didn't get that. Please try again.",
    };
  }
}

async function requestTextHandoff(
  apiBaseUrl: string,
  widgetKey: string,
  sessionId: string,
  reason?: string
): Promise<ResponseMessage> {
  const response = await fetch(`${apiBaseUrl}/chat/handoff/request`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-widget-key': widgetKey,
    },
    body: JSON.stringify({
      widget_key: widgetKey,
      session_id: sessionId,
      reason: reason || '',
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.message || data?.error || 'Failed to request handoff');
  }

  return {
    content: data?.output || 'A human agent has been notified.',
    handoff: data?.handoff
      ? {
          active: true,
          id: data.handoff.id as string | undefined,
          status: data.handoff.status,
        }
      : undefined,
  };
}

type HandoffStatusResponse = {
  success: boolean;
  active: boolean;
  awaiting_agent_reply?: boolean;
  wait_remaining_ms?: number;
  handoff: null | {
    id: string;
    status: 'waiting_for_agent' | 'assigned' | 'closed';
    output: string;
    assigned_user?: {
      id: string;
      name: string;
      email: string;
    } | null;
  };
  messages: Array<{
    id: string;
    role: 'assistant' | 'system';
    content: string;
    created_at: string;
    source?: 'widget' | 'n8n' | 'system' | null;
    type?: string | null;
    agent_name?: string | null;
  }>;
};

async function fetchTextHandoffStatus(
  apiBaseUrl: string,
  widgetKey: string,
  sessionId: string,
  handoffId?: string | null
): Promise<HandoffStatusResponse> {
  let url = `${apiBaseUrl}/chat/handoff/status?session_id=${encodeURIComponent(sessionId)}`;
  if (handoffId) url += `&handoff_id=${encodeURIComponent(handoffId)}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'x-widget-key': widgetKey,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch handoff status: ${response.statusText}`);
  }

  return response.json();
}

async function notifyTextHandoffPresence(
  apiBaseUrl: string,
  widgetKey: string,
  sessionId: string,
  state: 'active' | 'left',
  reason?: string,
  useBeacon = false
) {
  const url = `${apiBaseUrl}/chat/handoff/presence`;
  const payload = JSON.stringify({
    widget_key: widgetKey,
    session_id: sessionId,
    state,
    reason: reason || '',
  });

  if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([payload], { type: 'application/json' });
    navigator.sendBeacon(url, blob);
    return;
  }

  await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-widget-key': widgetKey,
    },
    body: payload,
    keepalive: useBeacon,
  });
}

export {
  fetchChatResponse,
  fetchTextHandoffStatus,
  notifyTextHandoffPresence,
  requestTextHandoff,
  resolveApiBaseUrl,
};
